import type { IBounds } from "../screens/Dashboard/SubComponents/Studio/interfaces/sub-interfaces/Bounds.interface";

import type { IAudioVisualizerSourceConfig } from "../screens/Dashboard/SubComponents/Studio/interfaces/sub-interfaces/Widgets.interface";

export enum WidgetsType {
  BROWSER = "Browser",

  SELFHOST = "Selfhost",

  AUDIO_VISUALIZER = "AudioVisualizer",

  GUEST = "Guest",

  AGENT = "Agent",

  BOT = "Bot",

  TRIGGERS = "Triggers",
}

export type TriggerEvent =
  | "stream_started"
  | "stream_stopped"
  | "scene_switched"
  | "timer_reached"
  | "scheduled"
  | "track_ended"
  | "playlist_started"
  | "playlist_stopped"
  | "playlist_ended"
  | "mic_muted"
  | "mic_unmuted"
  | "hotkey"
  | "event_follow"
  | "event_sub"
  | "event_donation"
  | "event_raid"
  | "event_chat";

export type TriggerAction =
  | "switch_scene"
  | "toggle_area"
  | "play_media"
  | "pause_media"
  | "change_text"
  | "webhook"
  | "playlist_play"
  | "playlist_pause"
  | "playlist_next"
  | "playlist_prev"
  | "mic_mute"
  | "mic_unmute"
  | "enable_protected_mode"
  | "disable_protected_mode"
  | "enable_system_audio"
  | "disable_system_audio"
  | "clone_area"
  | "hide_area"
  | "show_area"
  | "hide_widget"
  | "show_widget"
  | "hide_element"
  | "show_element"
  | "send_chat_message"
  | "delete_message"
  | "timeout_user"
  | "ban_user";

export interface ITriggerRule {
  id: string;

  name: string;

  enabled: boolean;

  once?: boolean;

  event: TriggerEvent;

  eventConfig: Record<string, string>;

  action: TriggerAction;

  actionConfig: Record<string, string>;
}

export interface IWidgetBaseDB {
  id?: number;

  name: string;

  type: WidgetsType;

  configuration: null;
}

export interface IWidgetBrowser extends Omit<IWidgetBaseDB, "configuration"> {
  id?: number;

  configuration: {
    bounds: IBounds;

    src: string;
  };
}

export interface IWidgetSelfhost extends Omit<IWidgetBaseDB, "configuration"> {
  id?: number;

  configuration: {
    host: string;

    username: string;

    password: string;
  };
}

export interface IWidgetAudioVisualizer
  extends Omit<IWidgetBaseDB, "configuration"> {
  id?: number;

  configuration: {
    sources: IAudioVisualizerSourceConfig[];

    backgroundColor: string;

    barCount: number;

    barGap: number;

    barRadius: number;

    smoothing: number;

    fftSize: number;
  };
}

export interface IWidgetGuest extends Omit<IWidgetBaseDB, "configuration"> {
  id?: number;

  configuration: {
    deviceId: string;

    label: string;

    romId?: string;
  };
}

export interface IWidgetAgent extends Omit<IWidgetBaseDB, "configuration"> {
  id?: number;

  configuration: {
    roomName: string;

    agentName: string;

    systemPrompt: string;

    language: string;
  };
}

export interface IWidgetTriggers extends Omit<IWidgetBaseDB, "configuration"> {
  id?: number;

  configuration: {
    rules: ITriggerRule[];
  };
}

export type IWidgetDB =
  | IWidgetBaseDB
  | IWidgetBrowser
  | IWidgetSelfhost
  | IWidgetAudioVisualizer
  | IWidgetGuest
  | IWidgetAgent
  | IWidgetTriggers;

export type IWidgetsDB = IWidgetDB[];
